import React, { useCallback, useEffect, useState, useRef } from "react";
import { Button, Modal, Spinner } from "@themesberg/react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPrint, faTimes, faExclamationTriangle } from "@fortawesome/free-solid-svg-icons";
import { postApi, fetchApi, getSector } from "../../../redux/api";
import { formatNumber } from "../../../redux/utils";
import moment from "moment";
import toast from "react-hot-toast";

const pageStyle = {
  width: "210mm",
  minHeight: "297mm",
  padding: "20px 50px 20px 50px",
  fontFamily: "Arial, sans-serif",
  fontSize: "10px",
  backgroundColor: "white",
  margin: "0 auto 20px",
  boxShadow: "0 0 10px rgba(0,0,0,0.1)",
  position: "relative",
  boxSizing: "border-box",
};

const pageBreakStyle = {
  ...pageStyle,
  pageBreakBefore: "always",
};

const watermarkStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%) rotate(-45deg)",
  fontSize: "48px",
  fontWeight: "bold",
  color: "rgba(0, 0, 0, 0.05)",
  whiteSpace: "nowrap",
  pointerEvents: "none",
  userSelect: "none",
  zIndex: 1,
};

const contentWrapperStyle = {
  position: "relative",
  zIndex: 2,
  display: "flex",
  flexDirection: "column",
  height: "100%",
};

const PageHeader = () => (
  <div style={{ display: "flex", flexDirection: "column", alignItems: "center", paddingTop: "20px", marginBottom: "20px" }}>
    <img src={require("../../../assets/img/KTSG.png")} style={{ width: "100px", height: "60px", marginBottom: "8px" }} alt="logo" />
    <div style={{ fontSize: "17px", fontWeight: "bold", marginTop: "10px" }}>
      MINISTRY OF BUDGET &amp; ECONOMIC PLANNING
    </div>
    <div style={{ fontSize: "10px", fontWeight: "bold" }}>
      Old Government House Office Complex, Katsina
    </div>
    <div style={{ fontSize: "12px", fontStyle: "italic" }}>
      P.MB 2132, KATSINA
    </div>
  </div>
);

const RefDateRow = ({ form, taday }) => (
  <div style={{ display: "flex", justifyContent: "space-between", paddingBottom: "10px", marginTop: "10px" }}>
    <div style={{ width: "70%", fontWeight: "bold" }}>
      <span>Our Ref: </span>
      <span style={{ textDecoration: "underline" }}>{form?.approval_no}</span>
    </div>
    <div style={{ width: "30%", textDecoration: "underline", fontWeight: "bold", textAlign: "right" }}>
      {taday}
    </div>
  </div>
);

const SubjectTitle = () => (
  <div style={{ width: "100%", textAlign: "center", marginTop: "10px" }}>
    <div style={{ textDecoration: "underline", fontWeight: "bold", fontSize: "11px", textTransform: "uppercase" }}>
      SPECIAL RELEASE OF FUNDS
    </div>
  </div>
);

const SignatureBlock = ({ form, directorName, directorSignature, align = "right" }) => (
  <div style={{ width: "35%", textAlign: align, marginLeft: align === "right" ? "auto" : undefined }}>
    {directorSignature && (
      <img src={directorSignature} style={{ width: "80px", height: "60px", marginBottom: "5px" }} alt="signature" />
    )}
    <div style={{ fontWeight: "bold", fontSize: "10px", marginBottom: "2px", textAlign: "center" }}>
      {directorName || "KABIR ABU BAZARIYE"}
    </div>
    <div style={{ fontWeight: "bold", fontSize: "9px", textAlign: "center" }}>
      DIRECTOR OF BUDGET
    </div>
    <div style={{ fontWeight: "bold", fontSize: "9px", textAlign: "center" }}>
      FOR: HON. COMMISSIONER
    </div>
  </div>
);

const CcSection = ({ form, ignoreMDA }) => (
  <div style={{ width: "60%", fontSize: "10px" }}>
    <div style={{ fontWeight: "bold", marginBottom: "8px" }}>CC:</div>
    <div style={{ marginLeft: "15px", fontStyle: "italic", fontSize: "8px" }}>
      {!ignoreMDA && form?.copies?.officeTitle && (
        <div style={{ marginBottom: "1px" }}>{form?.copies?.officeTitle}, Katsina,</div>
      )}
      {form?.copies1?.officeTitle && (
        <div style={{ marginBottom: "1px" }}>{form?.copies1?.officeTitle}, Katsina,</div>
      )}
      {form?.beneficiary?.officeTitle && (
        <div style={{ marginBottom: "1px" }}>{form?.beneficiary?.officeTitle}, Katsina,</div>
      )}
      <div style={{ marginBottom: "1px" }}>The Permanent Secretary, Government House, Katsina,</div>
      <div style={{ marginBottom: "1px" }}>The Hon. Commissioner, Ministry of Finance, Katsina,</div>
      <div style={{ marginBottom: "1px" }}>The Auditor General, Office of the Auditor-General for the State, Katsina,</div>
      <div style={{ marginBottom: "1px" }}>The Chief of Staff, Government House, Katsina,</div>
      <div style={{ marginBottom: "1px" }}>The Chairman, Fiscal Responsibility Commission, Katsina,</div>
      <div style={{ marginBottom: "1px" }}>The Director Main-Account, Ministry of Finance, Katsina,</div>
      {form.cc === "kirs" && (
        <div style={{ marginBottom: "1px" }}>Board Of Internal Revenue, Katsina,</div>
      )}
    </div>
  </div>
);

const ReleaseDocument = ({ form, tableData, directorName, directorSignature, ignoreMDA, taday }) => {
  const totalAmount = (tableData || []).reduce((acc, curr) => acc + (parseFloat(curr.amount) || 0), 0);

  return (
    <div style={{ ...pageStyle, position: "relative" }}>
      <div style={watermarkStyle}>Ministry of Budget and Economic Planning</div>
      <div style={contentWrapperStyle}>
        <PageHeader />
        <RefDateRow form={form} taday={taday} />

        <div style={{ width: "50%", marginBottom: "20px", marginTop: "10px" }}>
          <div style={{ fontStyle: "italic", fontSize: "11px", margin: "3px 0" }}>The Accountant General,</div>
          <div style={{ fontStyle: "italic", fontSize: "11px", margin: "3px 0" }}>Ministry of Finance,</div>
          <div style={{ fontStyle: "italic", fontSize: "11px", margin: "3px 0" }}>Katsina,</div>
        </div>

        <SubjectTitle />

        <div style={{ width: "100%", textAlign: "justify", marginTop: "7px", marginBottom: "20px" }}>
          {tableData && tableData.length > 1 ? (
            <>
              <p style={{ fontSize: "11px", lineHeight: "1.5", margin: "15px 0" }}>
                I am directed to convey approval for the release of funds for recurrent services/special programs in respect of {form.narration || 'the approved projects'} to Ministries, Departments and Agencies as follows:
              </p>
              <table style={{ width: "100%", borderCollapse: "collapse", marginTop: "10px", fontSize: "10px" }}>
                <thead style={{ backgroundColor: "#f0f0f0" }}>
                  <tr>
                    <th style={{ border: "1px solid #ccc", padding: "5px" }}>MDA Code</th>
                    <th style={{ border: "1px solid #ccc", padding: "5px" }}>Economic Code</th>
                    <th style={{ border: "1px solid #ccc", padding: "5px" }}>Description</th>
                    <th style={{ border: "1px solid #ccc", padding: "5px", textAlign: "right" }}>Amount (N)</th>
                  </tr>
                </thead>
                <tbody>
                  {tableData.map((item, i) => (
                    <tr key={i}>
                      <td style={{ border: "1px solid #ccc", padding: "5px" }}>{item.mda_code}</td>
                      <td style={{ border: "1px solid #ccc", padding: "5px" }}>{item.mda_economic_code || item.economic_code}</td>
                      <td style={{ border: "1px solid #ccc", padding: "5px" }}>{item.description_code || item.description}</td>
                      <td style={{ border: "1px solid #ccc", padding: "5px", textAlign: "right" }}>{formatNumber(item.amount)}</td>
                    </tr>
                  ))}
                  <tr style={{ fontWeight: "bold" }}>
                    <td colSpan="3" style={{ border: "1px solid #ccc", padding: "5px", textAlign: "right" }}>TOTAL:</td>
                    <td style={{ border: "1px solid #ccc", padding: "5px", textAlign: "right" }}>N{formatNumber(totalAmount)}</td>
                  </tr>
                </tbody>
              </table>
            </>
          ) : (
            <div>
              <p style={{ fontSize: "11px", lineHeight: "1.5", margin: "15px 0" }}>
                I am directed to convey approval for the release of{" "}
                <span style={{ fontWeight: "bold" }}>(N{formatNumber(form.amount)}) only</span>{" "}
                to {form?.copies1?.officeTitle}
                {form?.beneficiary?.officeTitle && form?.beneficiary?.officeTitle !== null
                  ? " payable to " : ""}{" "}
                {form?.beneficiary?.officeTitle && form?.beneficiary?.officeTitle !== null
                  ? form?.beneficiary?.officeTitle + " " : ""}
                {form.narration}.
              </p>
              <p style={{ marginTop: "10px", fontWeight: "bold", fontSize: "11px", lineHeight: "1.5" }}>
                The Vote of Charge is{" "}
                {form.project_type === "payable-to" ? form.send_to_office : form.mda_code}/
                {form.approval_type === "Recurrent" ? form.economic_code : form.program_code}.
              </p>
            </div>
          )}
          <p style={{ fontSize: "11px", lineHeight: "1.5", margin: "15px 0" }}>
            This letter is copied to {ignoreMDA ? null : form?.copies?.officeTitle}{" "}
            {form?.copies1?.officeTitle ? form?.copies1?.officeTitle + ", " : ""}
            {form?.beneficiary?.officeTitle ? form?.beneficiary?.officeTitle + ", " : ""}
            {form?.cc ? form?.cc + ", " : ""}
            the PS, (GHKT), the HC (MOF), the COS (GHKT), the Chairman (FRC),
            the Auditor-General, the Office of the Auditor-General for the
            State, the Chairman, and the Director Main Account, Ministry of
            Finance.
          </p>
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", margin: "20px 0" }}>
          <SignatureBlock form={form} directorName={directorName} directorSignature={directorSignature} align="center" />
        </div>

        <div style={{ display: "flex", justifyContent: "space-between", marginTop: "20px" }}>
          <CcSection form={form} ignoreMDA={ignoreMDA} />
          <SignatureBlock form={form} directorName={directorName} directorSignature={directorSignature} />
        </div>

        <div style={{ textAlign: "center", fontSize: "10px", fontStyle: "italic", marginTop: "15px", marginBottom: "10px" }}>
          Above for your information and awareness, please.
        </div>
      </div>
    </div>
  );
};

function ReleasePrintPreviewModal({ show, onHide, approvalNos = [] }) {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [directors, setDirectors] = useState([]);
  const [logoUrl, setLogoUrl] = useState("");
  const printContentRef = useRef(null);

  useEffect(() => {
    const logo = require("../../../assets/img/KTSG.png");
    if (typeof logo === "string") {
      setLogoUrl(logo);
    }
  }, []);

  useEffect(() => {
    getSector({}, "select_director", setDirectors);
  }, []);

  const fetchDocument = useCallback(async (approvalNo) => {
    try {
      const data = await postApi("post_approval_collection", {
        form: { query_type: "select_by_approval_nos", approval_no: approvalNo },
      });

      if (!data?.result?.length) {
        return { approvalNo, error: "No data found", form: null, tableData: [] };
      }

      const tableData = data.result;
      const form = data.result[0];
      const taday = moment(form.approval_date).format("Do MMMM, YYYY");
      const ignoreMDA =
        form.mda_name === "Ministry of Finance" || form.mda_name === "Government House";

      let copiesData = { copies1: null, copies: null, beneficiary: null };
      try {
        const ccResp = await postApi("post-cc?query_type=select_mda", { mda_name: form.mda_name });
        if (ccResp?.results?.length) {
          copiesData.copies1 = ccResp.results[0];
        }
      } catch (e) { /* ignore */ }

      if (form.mda_head) {
        try {
          const ccResp = await postApi("post-cc?query_type=select_mda", { mda_name: form.mda_head });
          if (ccResp?.results?.length) {
            copiesData.beneficiary = ccResp.results[0];
          }
        } catch (e) { /* ignore */ }
      }

      let signatureURL = "";
      if (form.approved_ids) {
        try {
          const sigResp = await fetchApi(`sign-by-id-new?id=${form.approved_ids}`);
          if (sigResp?.success && sigResp?.data?.[0]) {
            signatureURL = sigResp.data[0].imageURL || sigResp.data[0].signature || "";
          }
        } catch (e) { /* ignore */ }
      }

      const directorName = directors[0]?.name;
      const directorSignature = directors[0]?.imageURL || signatureURL;

      return {
        approvalNo,
        error: null,
        form: { ...form, ...copiesData },
        tableData,
        taday,
        ignoreMDA,
        directorName,
        directorSignature,
      };
    } catch (err) {
      console.error(`Error fetching ${approvalNo}:`, err);
      return { approvalNo, error: err.message, form: null, tableData: [] };
    }
  }, [directors]);

  useEffect(() => {
    if (!show || !approvalNos.length) return;

    const fetchAll = async () => {
      setLoading(true);
      setDocuments([]);
      try {
        const results = await Promise.all(approvalNos.map(fetchDocument));
        setDocuments(results);
      } catch (err) {
        toast.error("Failed to load some documents");
      } finally {
        setLoading(false);
      }
    };

    fetchAll();
  }, [show, approvalNos, fetchDocument]);

  const handlePrint = () => {
    const printWindow = window.open("", "_blank");
    if (!printWindow) {
      toast.error("Please allow pop-ups to print");
      return;
    }

      const allContent = documents.map((doc, index) => {
      if (doc.error || !doc.form) {
        return `<div style="padding:20px;margin:20px;border:1px solid #ccc;">
          <p>Failed to load document: ${doc.approvalNo}</p>
          <p>${doc.error}</p>
        </div>`;
      }

      const form = doc.form;
      const pageBreakStyleCss = index > 0 ? "page-break-before: always;" : "";

      return renderDocumentHTML(doc, pageBreakStyleCss, logoUrl);
    }).join("");

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Release Documents - Print Preview</title>
        <style>
          @page { size: A4; margin: 0; }
          body { margin: 0; padding: 0; font-family: Arial, sans-serif; }
          @media print {
            .no-print { display: none !important; }
          }
        </style>
      </head>
      <body>
        ${allContent}
      </body>
      </html>
    `);
    printWindow.document.close();

    setTimeout(() => {
      printWindow.focus();
      printWindow.print();
    }, 500);
  };

  const objToCss = (obj) => {
    if (!obj) return "";
    return Object.entries(obj)
      .map(([key, val]) => {
        const cssKey = key.replace(/([A-Z])/g, "-$1").toLowerCase();
        return `${cssKey}: ${typeof val === "number" ? val + "px" : val}`;
      })
      .join("; ");
  };

  const renderDocumentHTML = (doc, pageBreakStyleCss, logoUrl) => {
    if (!doc.form) return "";
    const form = doc.form;
    const tableData = doc.tableData || [];
    const totalAmount = tableData.reduce((acc, curr) => acc + (parseFloat(curr.amount) || 0), 0);
    const taday = doc.taday;
    const ignoreMDA = doc.ignoreMDA;
    const directorName = doc.directorName || "KABIR ABU BAZARIYE";
    const directorSignature = doc.directorSignature || "";

    const pageCss = objToCss(pageStyle);

    const bodyContent = tableData.length > 1
      ? `
        <p style="font-size:11px;line-height:1.5;margin:15px 0;">
          I am directed to convey approval for the release of funds for recurrent services/special programs in respect of ${form.narration || 'the approved projects'} to Ministries, Departments and Agencies as follows:
        </p>
        <table style="width:100%;border-collapse:collapse;margin-top:10px;font-size:10px;">
          <thead style="background-color:#f0f0f0;">
            <tr>
              <th style="border:1px solid #ccc;padding:5px;">MDA Code</th>
              <th style="border:1px solid #ccc;padding:5px;">Economic Code</th>
              <th style="border:1px solid #ccc;padding:5px;">Description</th>
              <th style="border:1px solid #ccc;padding:5px;text-align:right;">Amount (N)</th>
            </tr>
          </thead>
          <tbody>
            ${tableData.map(item => `
              <tr>
                <td style="border:1px solid #ccc;padding:5px;">${item.mda_code || ''}</td>
                <td style="border:1px solid #ccc;padding:5px;">${item.mda_economic_code || item.economic_code || ''}</td>
                <td style="border:1px solid #ccc;padding:5px;">${item.description_code || item.description || ''}</td>
                <td style="border:1px solid #ccc;padding:5px;text-align:right;">${formatNumber(item.amount)}</td>
              </tr>
            `).join('')}
            <tr style="font-weight:bold;">
              <td colspan="3" style="border:1px solid #ccc;padding:5px;text-align:right;">TOTAL:</td>
              <td style="border:1px solid #ccc;padding:5px;text-align:right;">N${formatNumber(totalAmount)}</td>
            </tr>
          </tbody>
        </table>`
      : `
        <div>
          <p style="font-size:11px;line-height:1.5;margin:15px 0;">
            I am directed to convey approval for the release of
            <span style="font-weight:bold;">(N${formatNumber(form.amount)}) only</span>
            to ${form?.copies1?.officeTitle || ''}
            ${form?.beneficiary?.officeTitle ? ' payable to ' : ''}
            ${form?.beneficiary?.officeTitle || ''}
            ${form.narration || ''}.
          </p>
          <p style="margin-top:10px;font-weight:bold;font-size:11px;line-height:1.5;">
            The Vote of Charge is
            ${form.project_type === "payable-to" ? form.send_to_office : form.mda_code}/
            ${form.approval_type === "Recurrent" ? form.economic_code : form.program_code}.
          </p>
        </div>`;

    const ccItems = [
      ...(!ignoreMDA && form?.copies?.officeTitle ? [`${form.copies.officeTitle}, Katsina,`] : []),
      ...(form?.copies1?.officeTitle ? [`${form.copies1.officeTitle}, Katsina,`] : []),
      ...(form?.beneficiary?.officeTitle ? [`${form.beneficiary.officeTitle}, Katsina,`] : []),
      "The Permanent Secretary, Government House, Katsina,",
      "The Hon. Commissioner, Ministry of Finance, Katsina,",
      "The Auditor General, Office of the Auditor-General for the State, Katsina,",
      "The Chief of Staff, Government House, Katsina,",
      "The Chairman, Fiscal Responsibility Commission, Katsina,",
      "The Director Main-Account, Ministry of Finance, Katsina,",
      ...(form.cc === "kirs" ? ["Board Of Internal Revenue, Katsina,"] : []),
    ];

    return `
    <div style="${pageCss}; ${pageBreakStyleCss} position:relative;">
      <div style="position:absolute;top:50%;left:50%;transform:translate(-50%,-50%)rotate(-45deg);font-size:48px;font-weight:bold;color:rgba(0,0,0,0.05);white-space:nowrap;pointer-events:none;user-select:none;z-index:1;">
        Ministry of Budget and Economic Planning
      </div>
      <div style="position:relative;z-index:2;display:flex;flex-direction:column;height:100%;">
        <div style="display:flex;flex-direction:column;align-items:center;padding-top:20px;margin-bottom:20px;">
          <img src="${logoUrl}" style="width:100px;height:60px;margin-bottom:8px;" alt="logo" />
          <div style="font-size:17px;font-weight:bold;margin-top:10px;">MINISTRY OF BUDGET &amp; ECONOMIC PLANNING</div>
          <div style="font-size:10px;font-weight:bold;">Old Government House Office Complex, Katsina</div>
          <div style="font-size:12px;font-style:italic;">P.MB 2132, KATSINA</div>
        </div>
        <div style="display:flex;justify-content:space-between;padding-bottom:10px;margin-top:10px;">
          <div style="width:70%;font-weight:bold;"><span>Our Ref: </span><span style="text-decoration:underline;">${form?.approval_no || ''}</span></div>
          <div style="width:30%;text-decoration:underline;font-weight:bold;text-align:right;">${taday || ''}</div>
        </div>
        <div style="width:50%;margin-bottom:20px;margin-top:10px;">
          <div style="font-style:italic;font-size:11px;margin:3px 0;">The Accountant General,</div>
          <div style="font-style:italic;font-size:11px;margin:3px 0;">Ministry of Finance,</div>
          <div style="font-style:italic;font-size:11px;margin:3px 0;">Katsina,</div>
        </div>
        <div style="width:100%;text-align:center;margin-top:10px;">
          <div style="text-decoration:underline;font-weight:bold;font-size:11px;text-transform:uppercase;">SPECIAL RELEASE OF FUNDS</div>
        </div>
        <div style="width:100%;text-align:justify;margin-top:7px;margin-bottom:20px;">
          ${bodyContent}
          <p style="font-size:11px;line-height:1.5;margin:15px 0;">
            This letter is copied to ${ignoreMDA ? '' : (form?.copies?.officeTitle || '')}
            ${form?.copies1?.officeTitle ? form.copies1.officeTitle + ', ' : ''}
            ${form?.beneficiary?.officeTitle ? form.beneficiary.officeTitle + ', ' : ''}
            ${form?.cc ? form.cc + ', ' : ''}
            the PS, (GHKT), the HC (MOF), the COS (GHKT), the Chairman (FRC),
            the Auditor-General, the Office of the Auditor-General for the State,
            the Chairman, and the Director Main Account, Ministry of Finance.
          </p>
        </div>
        <div style="display:flex;justify-content:flex-end;margin:20px 0;">
          <div style="width:35%;text-align:center;">
            ${directorSignature ? `<img src="${directorSignature}" style="width:80px;height:60px;margin-bottom:5px;" alt="sig" />` : ''}
            <div style="font-weight:bold;font-size:10px;margin-bottom:2px;text-align:center;">${directorName}</div>
            <div style="font-weight:bold;font-size:9px;text-align:center;">DIRECTOR OF BUDGET</div>
            <div style="font-weight:bold;font-size:9px;text-align:center;">FOR: HON. COMMISSIONER</div>
          </div>
        </div>
        <div style="display:flex;justify-content:space-between;margin-top:20px;">
          <div style="width:60%;font-size:10px;">
            <div style="font-weight:bold;margin-bottom:8px;">CC:</div>
            <div style="margin-left:15px;font-style:italic;font-size:8px;">
              ${ccItems.map(item => `<div style="margin-bottom:1px;">${item}</div>`).join('')}
            </div>
          </div>
          <div style="width:35%;text-align:right;">
            ${directorSignature ? `<img src="${directorSignature}" style="width:80px;height:60px;margin-bottom:5px;" alt="sig" />` : ''}
            <div style="font-weight:bold;font-size:10px;margin-bottom:2px;text-align:center;">${directorName}</div>
            <div style="font-weight:bold;font-size:9px;text-align:center;">DIRECTOR OF BUDGET</div>
            <div style="font-weight:bold;font-size:9px;text-align:center;">FOR: HON. COMMISSIONER</div>
          </div>
        </div>
        <div style="text-align:center;font-size:10px;font-style:italic;margin-top:15px;margin-bottom:10px;">
          Above for your information and awareness, please.
        </div>
      </div>
    </div>`;
  };

  const hasErrors = documents.some((d) => d.error || !d.form);

  return (
    <Modal show={show} onHide={onHide} size="xl" fullscreen="lg-down" scrollable>
      <Modal.Header closeButton>
        <Modal.Title>
          <FontAwesomeIcon icon={faPrint} className="me-2" />
          Print Preview — {approvalNos.length} Release{approvalNos.length > 1 ? "s" : ""}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ backgroundColor: "#f0f0f0", padding: "20px" }}>
        {loading ? (
          <div className="d-flex justify-content-center align-items-center" style={{ height: "400px" }}>
            <div className="text-center">
              <Spinner animation="border" variant="primary" />
              <p className="mt-3">Loading release documents...</p>
            </div>
          </div>
        ) : hasErrors ? (
          <div className="text-center py-5">
            <FontAwesomeIcon icon={faExclamationTriangle} size="3x" className="text-warning mb-3" />
            <h5>Some documents could not be loaded</h5>
            <p className="text-muted">Please try again or check the approval numbers.</p>
            <div className="mt-3">
              {documents.filter(d => d.error).map((d, i) => (
                <p key={i} className="text-danger mb-1">{d.approvalNo}: {d.error}</p>
              ))}
            </div>
          </div>
        ) : (
          <div ref={printContentRef}>
            {documents.map((doc, index) => (
              <div key={doc.approvalNo} style={index > 0 ? pageBreakStyle : pageStyle}>
                <ReleaseDocument
                  form={doc.form}
                  tableData={doc.tableData}
                  directorName={doc.directorName}
                  directorSignature={doc.directorSignature}
                  ignoreMDA={doc.ignoreMDA}
                  taday={doc.taday}
                />
              </div>
            ))}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          <FontAwesomeIcon icon={faTimes} className="me-1" />
          Close
        </Button>
        <Button variant="primary" onClick={handlePrint} disabled={loading || hasErrors}>
          <FontAwesomeIcon icon={faPrint} className="me-1" />
          Print All {approvalNos.length > 0 ? `(${approvalNos.length})` : ""}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default ReleasePrintPreviewModal;
